<template>
  <div :class="classes">
    <div ref="carousel" class="ui-carousel__carousel">
      <slot></slot>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

type Props = {
  options?: Object
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (
    e: 'update',
    state?: {
      current: number
      total: number
      previousDisabled: boolean
      nextDisabled: boolean
    },
  ): void
}>()

const carousel = ref<HTMLElement | null>(null)

let flkty = ref<any>(null)

const previous = () => flkty.value?.previous()
const next = () => flkty.value?.next()
const goTo = (index: number) => flkty.value?.select(index)

const reload = () => {
  if (!flkty.value) return

  flkty.value.reloadCells()
  flkty.value.resize()
}

const emitUpdate = () => {
  if (!flkty.value) {
    emit('update')

    return
  }

  const current = flkty.value.selectedIndex + 1
  const total = flkty.value.slides.length

  emit('update', {
    current,
    total,
    previousDisabled: current === 1,
    nextDisabled: current === total,
  })
}

const classes = computed(() => ({
  'ui-carousel': true,
}))

onMounted(async () => {
  if (!carousel.value) return

  const Flickity = (await import('flickity')).default
  flkty.value = new Flickity(carousel.value, {
    freeScroll: false,
    wrapAround: false,
    groupCells: true,
    autoPlay: false,
    cellAlign: 'left',
    contain: true,
    prevNextButtons: false,
    pageDots: false,
    adaptiveHeight: false,
    pauseAutoPlayOnHover: true,
    // selectedAttraction: 0.01,
    // friction: 0.15,
    on: { ready: emitUpdate },
    ...props.options,
  })

  flkty.value.on('change', emitUpdate)
  flkty.value.on('resize', emitUpdate)

  emitUpdate()
})

onBeforeUnmount(() => {
  if (!flkty.value) return

  flkty.value.off('change', emitUpdate)
  flkty.value.off('resize', emitUpdate)
  flkty.value.destroy()
  flkty.value = null

  emitUpdate()
})

defineExpose({ previous, next, goTo, reload })
</script>

<style lang="scss">
@import '../styles/config';
</style>
